import { useState, useMemo, useEffect, useRef, useId } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useDomainStore } from '@/stores/domainStore';
import { useSchool } from '@/contexts/SchoolContext';
import { updateDomain } from '@/services/domainRegistry';
import { normaliseType } from '@/services/domain/optionUtils';
import { mergeDomainFieldChanges } from '@/services/domain/fieldConfigurationMerge';
import { validateUserField } from '@/services/domain/schemaAssert';
import { canConfigureDomain, compareDomainConfigs } from '@/services/domain/centralModel';
import { useNotifications } from '@/components/notifications/NotificationSystem';
import Modal from '@/components/ui/Modal/Modal';
import { isActiveDomainSchemaItem } from '@/features/domains/utils/domainHelpers';
import { isAssessmentDomain } from '@/services/domain/recordingContract';
import PhaseApplicabilityField from '@/features/phases/components/PhaseApplicabilityField';
import usePhaseConfigurationView from '@/features/phases/usePhaseConfigurationView';
import { normalizePhaseApplicability } from '@/services/domain/phaseApplicability';
import { loadSchoolPhases } from '@/services/schoolPhasesService';

const FIELD_TYPES = [
  { value: 'number',      label: 'Number' },
  { value: 'text',        label: 'Text' },
  { value: 'binary',      label: 'Yes / No' },
  { value: 'multiChoice', label: 'Choice (coloured)' },
];

const FIELD_TYPE_LABELS = Object.freeze({
  number: 'Number',
  text: 'Text',
  string: 'Short text',
  boolean: 'Yes / No',
  binary: 'Yes / No',
  multiChoice: 'Choice',
  storageRef: 'File',
});

// daisyUI semantic tokens — match the theme, automatically dark-mode aware.
// Use these (not raw hex) so school-authored colours follow the active theme.
const OPTION_COLORS = [
  { value: 'success', label: 'Green',  swatch: 'bg-success'  },
  { value: 'warning', label: 'Amber',  swatch: 'bg-warning'  },
  { value: 'error',   label: 'Red',    swatch: 'bg-error'    },
  { value: 'info',    label: 'Blue',   swatch: 'bg-info'     },
  { value: 'primary', label: 'Brand',  swatch: 'bg-primary'  },
  { value: 'secondary', label: 'Secondary', swatch: 'bg-secondary' },
  { value: 'accent',  label: 'Accent', swatch: 'bg-accent'   },
  { value: 'neutral', label: 'Grey',   swatch: 'bg-neutral'  },
];

const newFieldId = () => `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
const slugifyValue = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
const emptyFieldDraft = () => ({
  label: '',
  type: 'number',
  maxScore: '',
  optionsDraft: [
    { value: '', label: '', color: 'success' },
    { value: '', label: '', color: 'warning' },
  ],
});

function conflictValue(value, phaseNames = {}, isPhase = false) {
  if (value === undefined) return 'Removed';
  if (value === null || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(item => conflictValue(item, phaseNames, isPhase)).join(', ') || 'None';
  if (isPhase && value?.mode) return value.mode === 'whole-school' ? 'Whole school'
    : conflictValue(value.phaseIds || [], phaseNames, true);
  if (isPhase && typeof value === 'string') return value === 'whole-school' ? 'Whole school'
    : value === 'selected-phases' ? 'Selected phases' : phaseNames[value] || 'Unavailable phase';
  if (typeof value === 'object') return [value.label, FIELD_TYPE_LABELS[value.type],
    value.maxScore != null ? `maximum ${value.maxScore}` : null,
    Array.isArray(value.options) ? value.options.map(option => option.label).join(', ') : null,
  ].filter(Boolean).join(' · ') || 'Field settings';
  return String(value);
}

function conflictLabel(conflict, schema) {
  const [section, identity] = conflict.path;
  const field = section === 'primary' ? schema.primary : schema.followUp?.find(item => item.id === identity);
  const property = conflict.path.at(-1);
  const names = { label: 'Name', maxScore: 'Maximum value', weight: 'Contribution', optional: 'Optional question',
    color: 'Colour', options: 'Choices', showWhen: 'When to show', type: 'Input method' };
  const optionIndex = conflict.path.indexOf('options');
  const option = optionIndex >= 0 && field?.options?.find(item => item.value === conflict.path[optionIndex + 1]);
  return [field?.label || 'Event fields', option?.label,
    conflict.path.includes('phaseApplicability') ? 'Phase availability' : names[property]].filter(Boolean).join(' · ');
}

function editableField(field) {
  if (!isActiveDomainSchemaItem(field)) return null;
  return { ...field, ...(Array.isArray(field.options)
    ? { options: field.options.filter(isActiveDomainSchemaItem) } : {}) };
}

// Save on blur while keeping an in-progress value through live form updates.
function FieldValueInput({ value, onCommit, ...props }) {
  const [draftValue, setDraftValue] = useState(String(value ?? ''));
  const inputRef = useRef(null);
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraftValue(String(value ?? ''));
  }, [value]);
  return <input
    {...props}
    ref={inputRef}
    value={draftValue}
    onChange={(event) => setDraftValue(event.target.value)}
    onBlur={async () => {
      if (draftValue.trim() === String(value ?? '').trim()) return;
      const accepted = await onCommit(draftValue);
      if (accepted === false) setDraftValue(String(value ?? ''));
    }}
    onKeyDown={(event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
      }
    }}
  />;
}

// One row of a multiChoice field's options. Keeps the label in local state so
// typing never round-trips through Firestore — only commits on blur. Color
// commits immediately (atomic select change).
function OptionRow({ fieldId, optionIndex, option, hidden, busy, canRemove, onCommit, onColor, onRemove, onPhase }) {
  const [label, setLabel] = useState(option.label || '');
  // Keep local in sync if the canonical option label changes from elsewhere.
  // Skip when this row is focused so we don't clobber the user's typing.
  const inputRef = useRef(null);
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setLabel(option.label || '');
  }, [option.label]);

  const commitIfChanged = async () => {
    if ((label || '').trim() === (option.label || '').trim()) return;
    const accepted = await onCommit(fieldId, optionIndex, label);
    if (accepted === false) setLabel(option.label || '');
  };

  return (
    <li hidden={hidden} className="min-w-0 space-y-1">
      <div className="grid min-w-0 grid-cols-[0.75rem_minmax(0,1fr)_auto] items-center gap-2 sm:flex">
      <span className={`w-3 h-3 rounded-full ${OPTION_COLORS.find(c => c.value === option.color)?.swatch || 'bg-neutral'}`} />
      <input
        ref={inputRef}
        type="text"
        className="input input-bordered input-xs min-w-0 w-full sm:flex-1"
        value={label}
        placeholder="Label"
        onChange={(e) => setLabel(e.target.value)}
        onBlur={commitIfChanged}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
      />
      <select
        className="select select-bordered select-xs col-span-2 col-start-2 row-start-2 min-w-0 w-full sm:col-auto sm:row-auto sm:w-28"
        value={option.color || 'neutral'}
        onChange={(e) => onColor(fieldId, optionIndex, e.target.value)}
        disabled={busy}
      >
        {OPTION_COLORS.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
      {canRemove && (
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square col-start-3 row-start-1 sm:col-auto sm:row-auto"
          onClick={() => onRemove(fieldId, optionIndex)}
          disabled={busy}
          aria-label="Remove choice"
        >
          <TrashIcon className="w-3 h-3 text-error/70" />
        </button>
      )}
      </div>
      {onPhase && <button type="button" className="btn btn-ghost btn-xs ml-5"
        disabled={busy} aria-label={`Phase availability for ${option.label}`}
        onClick={() => onPhase(fieldId, option)}>
        Applies to: {option.phaseApplicability?.mode === 'selected-phases' ? 'Selected phases' : 'Whole school'}
      </button>}
    </li>
  );
}

function AddFieldDraftForm({
  formId,
  draft,
  setDraft,
  busy,
  updateDraftOption,
  removeDraftOption,
  addDraftOption,
  onSubmit,
}) {
  return (
    <form id={formId} onSubmit={onSubmit} className="space-y-3">
      <p className="text-sm text-base-content/60">Choose the input that matches the answer staff need to record.</p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="new-domain-field-label" className="text-xs font-medium">Field name</label>
        <input
          id="new-domain-field-label"
          type="text"
          className="input input-sm input-bordered w-full"
          placeholder="e.g. Vocab, Essay, Comment"
          value={draft.label}
          onChange={(event) => setDraft((current) => ({ ...current, label: event.target.value }))}
          autoFocus
        />
      </div>
      <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_6rem]">
        <div className="flex flex-col gap-1.5 flex-1">
          <label htmlFor="new-domain-field-type" className="text-xs font-medium">Type</label>
          <select
            id="new-domain-field-type"
            className="select select-sm select-bordered w-full"
            value={draft.type}
            onChange={(event) => setDraft((current) => ({ ...current, type: event.target.value, maxScore: '' }))}
          >
            {FIELD_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        {draft.type === 'number' && (
          <div className="flex flex-col gap-1.5 sm:w-24">
            <label htmlFor="new-domain-field-maximum" className="text-xs font-medium">Maximum value</label>
            <input
              id="new-domain-field-maximum"
              type="number"
              min="0.000001"
              step="any"
              className="input input-sm input-bordered w-full"
              placeholder="—"
              value={draft.maxScore}
              onChange={(event) => setDraft((current) => ({ ...current, maxScore: event.target.value }))}
            />
          </div>
        )}
      </div>
      {draft.type === 'multiChoice' && (
        <div className="flex flex-col gap-1.5 pt-1">
          <label className="text-xs font-medium">Choices</label>
          <p className="-mt-1 text-[11px] text-base-content/60">
            Each choice gets a colour. Schools see these as tappable states in registers and queues.
          </p>
          <ul className="space-y-1.5">
            {draft.optionsDraft.map((option, index) => (
              <li key={`draft-opt-${index}`} className="grid min-w-0 grid-cols-[0.75rem_minmax(0,1fr)_auto] items-center gap-2 sm:flex">
                <span className={`h-3 w-3 rounded-full ${OPTION_COLORS.find((color) => color.value === option.color)?.swatch || 'bg-neutral'}`} />
                <input
                  type="text"
                  className="input input-bordered input-xs min-w-0 w-full sm:flex-1"
                  aria-label={`New choice ${index + 1}`}
                  placeholder={`Choice ${index + 1}`}
                  value={option.label}
                  onChange={(event) => updateDraftOption(index, { label: event.target.value })}
                />
                <select
                  className="select select-bordered select-xs col-span-2 col-start-2 row-start-2 min-w-0 w-full sm:col-auto sm:row-auto sm:w-28"
                  aria-label={`New choice ${index + 1} colour`}
                  value={option.color || 'neutral'}
                  onChange={(event) => updateDraftOption(index, { color: event.target.value })}
                >
                  {OPTION_COLORS.map((color) => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                  ))}
                </select>
                {draft.optionsDraft.length > 2 && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square col-start-3 row-start-1 sm:col-auto sm:row-auto"
                    onClick={() => removeDraftOption(index)}
                    aria-label="Remove choice"
                  >
                    <TrashIcon className="h-3 w-3 text-error/70" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1 self-start"
            onClick={addDraftOption}
            disabled={busy}
          >
            <PlusIcon className="h-3 w-3" />
            Add choice
          </button>
        </div>
      )}
    </form>
  );
}

/**
 * Editor for a domain's `fields[]` array — the multi-input shape.
 *
 * Used both during creation and in the existing Domain's Event step. With
 * onChange it edits a local creation draft; otherwise it saves through the
 * governed registry. Both use canonical primary + followUp fields.
 */
export default function FieldsEditor({ domain: domainProp = null, ...props } = {}) {
  const { schoolId } = useSchool();
  const { activeDomain: storeActiveDomain } = useDomainStore();
  // `domain` prop (e.g. setup-only selection in DomainsPanel) overrides the
  // workspace activeDomain so this editor can target a domain that isn't the
  // user's current workspace.
  const activeDomain = domainProp ?? storeActiveDomain;
  return <DomainFieldsEditor
    key={`${schoolId}:${activeDomain?.id || 'new'}:${Boolean(props.supplementaryOnly)}`}
    schoolId={schoolId}
    activeDomain={activeDomain}
    {...props}
  />;
}

function DomainFieldsEditor({
  schoolId,
  activeDomain,
  onChange,
  onPendingChange,
  supplementaryOnly = false,
  addFieldOpen,
  onAddFieldOpenChange,
  replaceContentWhenAdding = false,
}) {
  const { showError, showSuccess } = useNotifications();
  const phaseView = usePhaseConfigurationView(activeDomain);

  const remoteFields = useMemo(() => {
    if (activeDomain?.primary) {
      const followUp = Array.isArray(activeDomain.followUp) ? activeDomain.followUp : [];
      return [...(supplementaryOnly ? [] : [activeDomain.primary]), ...followUp]
        .map(editableField)
        .filter(Boolean)
        .map((f, i) => ({ ...f, order: i }));
    }
    if (!activeDomain?.fields?.length) return [];
    return [...activeDomain.fields]
      .map(editableField)
      .filter(Boolean)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [activeDomain, supplementaryOnly]);

  // Keep later edits independent of a delayed live echo from an earlier save.
  const [pendingFields, setPendingFields] = useState(null);
  const fields = pendingFields ?? remoteFields;
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const saveChain = useRef(Promise.resolve());
  const baseFields = useRef(null);
  const acknowledgedDraft = useRef(null);
  const revision = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const [draft, setDraft] = useState(emptyFieldDraft);
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveError, setSaveError] = useState(null);
  const [saveConflict, setSaveConflict] = useState(null);
  const [focusedWithin, setFocusedWithin] = useState(false);
  const [phaseEditor, setPhaseEditor] = useState(null);
  const [phaseError, setPhaseError] = useState('');
  const [phaseNames, setPhaseNames] = useState({});
  const phaseFormId = useId();
  const beginEdit = () => {
    if (!onChange && !baseFields.current) baseFields.current = {
      primary: supplementaryOnly ? activeDomain?.primary : remoteFields[0],
      followUp: supplementaryOnly ? remoteFields : remoteFields.slice(1),
    };
  };
  useEffect(() => {
    // A local Firestore echo can precede a rejected write. Release our draft
    // only after the save has actually succeeded, so rollback cannot erase it.
    if (!busy && saveStatus !== 'error') {
      const echoed = pendingFields
        && compareDomainConfigs(remoteFields, pendingFields, { ignore: [] }).length === 0;
      if (echoed) setPendingFields(null);
      // A focused input can contain a later edit that has not blurred yet.
      // Keep its baseline even when the previous save's listener catches up.
      if (!focusedWithin && !phaseEditor && (!pendingFields || echoed)) {
        baseFields.current = null;
        acknowledgedDraft.current = null;
      }
    }
  }, [remoteFields, pendingFields, busy, saveStatus, focusedWithin, phaseEditor]);
  useEffect(() => {
    if (!saveConflict?.conflicts.some(conflict => conflict.path.includes('phaseApplicability'))) return undefined;
    let current = true;
    loadSchoolPhases(schoolId).then(({ phases }) => {
      if (current) setPhaseNames(Object.fromEntries(phases.map(phase => [phase.id, phase.name])));
    }).catch(() => { if (current) setPhaseNames({}); });
    return () => { current = false; };
  }, [saveConflict, schoolId]);
  const handleFocusCapture = () => { beginEdit(); setFocusedWithin(true); };
  const handleBlurCapture = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocusedWithin(false);
  };
  const [internalShowAddForm, setInternalShowAddForm] = useState(fields.length === 0 && !supplementaryOnly);
  const showAddForm = typeof addFieldOpen === 'boolean' ? addFieldOpen : internalShowAddForm;
  const setShowAddForm = (next) => {
    if (typeof addFieldOpen !== 'boolean') setInternalShowAddForm(next);
    onAddFieldOpenChange?.(next);
  };

  useEffect(() => {
    if (!showAddForm && !busy) setDraft(emptyFieldDraft());
  }, [showAddForm, busy]);

  const closeAddForm = () => {
    if (busy) return;
    setDraft(emptyFieldDraft());
    setShowAddForm(false);
  };

  useEffect(() => {
    onPendingChange?.(busy || Boolean(phaseEditor) || (showAddForm && Boolean(
      draft.label.trim() || draft.maxScore || draft.optionsDraft.some((option) => option.label.trim()),
    )));
  }, [busy, draft, showAddForm, phaseEditor, onPendingChange]);

  if (!activeDomain) {
    return <p className="text-sm text-base-content/50 py-4 text-center">Pick a domain first.</p>;
  }

  // Persist the field list as the canonical `{ primary, followUp }` shape the
  // renderer (DomainFollowUpForm) and schema (schemaAssert) both read. The
  // first field is the singular headline (`primary`, the Quick Issue column);
  // the rest are `followUp`. The obsolete `source` discriminator is stripped
  // and friendly type names are canonicalised (binary→boolean) before write.
  const persist = async (change) => {
    beginEdit();
    const nextFields = typeof change === 'function' ? change(fieldsRef.current) : change;
    if (!Array.isArray(nextFields) || (nextFields.length === 0 && !supplementaryOnly)) {
      showError('Add a field', 'A domain needs at least one input field.');
      return false;
    }
    const cleaned = nextFields.map((f, i) => {
      const rest = { ...f };
      delete rest.source;
      return { ...rest, type: normaliseType(rest.type), order: i };
    });
    const primary = supplementaryOnly ? baseFields.current?.primary ?? activeDomain.primary : cleaned[0];
    const followUp = supplementaryOnly ? cleaned : cleaned.slice(1);
    const invalidChoice = cleaned.find((field) => {
      if (field.type !== 'multiChoice') return false;
      const optionKeys = (field.options || []).map((option) => String(option.value || '').trim());
      const optionLabels = (field.options || []).map((option) => String(option.label || '').trim().toLowerCase());
      return optionKeys.length < 2
        || optionKeys.some((value) => !value)
        || new Set(optionKeys).size !== optionKeys.length
        || optionLabels.some((label) => !label)
        || new Set(optionLabels).size !== optionLabels.length;
    });
    if (invalidChoice) {
      showError('Invalid choices', `${invalidChoice.label} needs at least two uniquely named choices.`);
      return false;
    }
    const firstError = cleaned
      .map((f, i) => validateUserField(f, i === 0 && !supplementaryOnly ? 'primary' : `followUp[${i - 1}]`))
      .find(Boolean);
    if (firstError) {
      showError('Invalid field', firstError);
      return false;
    }
    fieldsRef.current = cleaned;
    setPendingFields(cleaned);
    const thisRevision = ++revision.current;
    setBusy(true);
    setSaveStatus('saving');
    setSaveError(null);
    setSaveConflict(null);
    // Serialize writes: the next full form includes all accepted local edits,
    // and cannot finish before an older form and then be overwritten by it.
    const saving = saveChain.current.then(async () => {
      if (onChange) return onChange({ primary, followUp });
      const submitted = { primary, followUp };
      let next = submitted;
      if (acknowledgedDraft.current) {
        // This draft may predate the previous response. Carry forward remote
        // changes that response merged, while applying only later local edits.
        const { merged, conflicts } = mergeDomainFieldChanges(acknowledgedDraft.current, submitted, baseFields.current);
        if (conflicts.length) throw Object.assign(new Error('This form changed in another session. Compare the changes before saving.'), {
          code: 'domain/field-conflict', conflicts, currentFields: baseFields.current, mergedFields: merged,
        });
        next = merged;
      }
      const saved = await updateDomain(schoolId, activeDomain.id, next, { baseFields: baseFields.current });
      if (saved?.primary) {
        // Advance inside the queue, before the next write starts. A rejected
        // write or a listener echo must never acknowledge our pending edits.
        const acknowledged = {
          primary: supplementaryOnly ? saved.primary : editableField(saved.primary),
          followUp: (saved.followUp || []).map(editableField).filter(Boolean),
        };
        baseFields.current = acknowledged;
        acknowledgedDraft.current = thisRevision === revision.current ? acknowledged : submitted;
      }
      return saved;
    });
    saveChain.current = saving.catch(() => {});
    try {
      const saved = await saving;
      if (mounted.current && thisRevision === revision.current) {
        if (!onChange && saved?.primary) {
          const acknowledged = [...(supplementaryOnly ? [] : [saved.primary]), ...(saved.followUp || [])]
            .filter(isActiveDomainSchemaItem).map((field, order) => ({ ...field, order,
              ...(Array.isArray(field.options) ? { options: field.options.filter(isActiveDomainSchemaItem) } : {}),
            }));
          fieldsRef.current = acknowledged;
          setPendingFields(acknowledged);
        }
        setSaveStatus('saved');
        window.setTimeout(() => {
          if (mounted.current && thisRevision === revision.current) setSaveStatus('idle');
        }, 1800);
      }
      return true;
    } catch (err) {
      console.error('Failed to update fields', err);
      if (mounted.current && thisRevision === revision.current) {
        const message = err.code === 'functions/aborted'
          ? 'Another save finished first. Retry to check it against your changes.'
          : err.message || 'Could not update fields.';
        setSaveError(message);
        if (err.code === 'domain/field-conflict') setSaveConflict(err);
        setSaveStatus('error');
        if (err.code !== 'domain/field-conflict') showError('Save failed', message);
      }
      // The accepted local form remains visible and retryable. False is
      // reserved for rejected validation, which should restore the old value.
      return null;
    } finally {
      if (mounted.current && thisRevision === revision.current) setBusy(false);
    }
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const label = draft.label.trim();
    if (!label) return;
    if (draft.type === 'number' && draft.maxScore !== ''
      && (!Number.isFinite(Number(draft.maxScore)) || Number(draft.maxScore) <= 0)) {
      showError('Invalid maximum', 'The maximum value must be a number greater than zero.');
      return;
    }
    const field = {
      id: newFieldId(),
      label,
      type: draft.type,
      optional: false,
      order: fields.length,
    };
    if (draft.type === 'number' && draft.maxScore && Number(draft.maxScore) > 0) {
      field.maxScore = Number(draft.maxScore);
    }
    if (draft.type === 'multiChoice') {
      const opts = draft.optionsDraft
        .map((o) => {
          const rawValue = (o.value || '').trim();
          const isTempValue = /^opt_/.test(rawValue);
          return {
            label: o.label.trim(),
            value: (!rawValue || isTempValue ? slugifyValue(o.label) : rawValue) || rawValue,
            color: o.color || 'neutral',
          };
        })
        .filter((o) => o.label && o.value);
      if (opts.length < 2) {
        showError('Need options', 'Add at least two choices for a Choice field.');
        return;
      }
      if (opts.some((option) => !option.value)
        || new Set(opts.map((option) => option.value)).size !== opts.length) {
        showError('Different choices needed', 'Each choice must have a different, usable name.');
        return;
      }
      field.options = opts;
    }
    const saved = await persist((current) => [...current, field]);
    if (!saved) return;
    setDraft(emptyFieldDraft());
    setShowAddForm(false);
    if (saved) showSuccess('Field added', label);
  };

  const handleRemove = async (id) => {
    const next = fieldsRef.current
      .filter((f) => f.id !== id)
      .map((f, i) => ({ ...f, order: i }));
    await persist(next);
  };

  const handleMove = async (id, direction) => {
    const currentIndex = fieldsRef.current.findIndex((field) => field.id === id);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= fields.length) return;
    const next = [...fieldsRef.current];
    [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
    await persist(next.map((field, index) => ({ ...field, order: index })));
  };

  const handleUpdateOption = async (fieldId, optionIndex, patch) => {
    const next = fieldsRef.current.map((f) => {
      if (f.id !== fieldId || f.type !== 'multiChoice') return f;
      const opts = [...(f.options || [])];
      opts[optionIndex] = { ...opts[optionIndex], ...patch };
      return { ...f, options: opts };
    });
    return persist(next);
  };

  const handleCommitOptionLabel = async (fieldId, optionIndex, label) => {
    const trimmed = (label || '').trim();
    const current = fieldsRef.current.find((f) => f.id === fieldId);
    const opt = current?.options?.[optionIndex];
    if (!opt) return false;
    if (!trimmed) {
      showError('Choice name required', 'A saved choice cannot have a blank name. Retire it instead if it is no longer used.');
      return false;
    }
    const duplicate = (current.options || []).some((candidate, index) => (
      index !== optionIndex
      && String(candidate.label || '').trim().toLowerCase() === trimmed.toLowerCase()
    ));
    if (duplicate) {
      showError('Different choices needed', 'Each choice needs a unique visible name.');
      return false;
    }
    const patch = { label: trimmed };
    // The stored value is a historical answer key. Once assigned it must not
    // change merely because an administrator improves the visible label.
    if (!opt.value) {
      const slug = slugifyValue(trimmed);
      patch.value = slug || `opt_${Date.now().toString(36)}`;
    }
    return handleUpdateOption(fieldId, optionIndex, patch);
  };

  const handleCommitOptionColor = async (fieldId, optionIndex, color) => {
    await handleUpdateOption(fieldId, optionIndex, { color });
  };

  const openPhaseEditor = (fieldId, option) => {
    beginEdit();
    setPhaseError('');
    setPhaseEditor({ fieldId, optionValue: option.value, label: option.label,
      value: option.phaseApplicability ?? { mode: 'whole-school', phaseIds: [] } });
  };
  const closePhaseEditor = () => {
    if (!busy) { setPhaseEditor(null); setPhaseError(''); }
  };
  const saveChoicePhases = async event => {
    event.preventDefault();
    if (!phaseEditor || busy) return;
    let phaseApplicability;
    try { phaseApplicability = normalizePhaseApplicability(phaseEditor.value); }
    catch (error) { setPhaseError(error.message); return; }
    const field = fieldsRef.current.find(item => item.id === phaseEditor.fieldId);
    const index = field?.options?.findIndex(option => option.value === phaseEditor.optionValue) ?? -1;
    if (index < 0) { setPhaseError('This choice is no longer available. Close this editor and review the saved form.'); return; }
    const result = await handleUpdateOption(phaseEditor.fieldId, index, { phaseApplicability });
    // Server failures retain the complete pending form and its existing review
    // controls. Close the dialog so those errors and conflict choices are visible.
    if (result !== false) { setPhaseEditor(null); setPhaseError(''); }
  };

  const handleAddOption = async (fieldId) => {
    const next = fieldsRef.current.map((f) => {
      if (f.id !== fieldId || f.type !== 'multiChoice') return f;
      const tempValue = `opt_${Date.now().toString(36)}`;
      const label = `Choice ${(f.options || []).length + 1}`;
      const opts = [...(f.options || []), { value: tempValue, label, color: 'neutral' }];
      return { ...f, options: opts };
    });
    await persist(next);
  };

  const handleCommitFieldLabel = async (fieldId, label) => {
    const trimmed = String(label || '').trim();
    if (!trimmed) {
      showError('Field name required', 'Every Event field needs a visible name.');
      return false;
    }
    return persist((current) => current.map((field) => (
      field.id === fieldId ? { ...field, label: trimmed } : field
    )));
  };

  const handleToggleOptional = async (fieldId, optional) => persist((current) => current.map((field) => (
    field.id === fieldId ? { ...field, optional } : field
  )));

  const handleCommitMaxScore = async (fieldId, rawValue) => {
    const value = String(rawValue || '').trim();
    if (value && (!Number.isFinite(Number(value)) || Number(value) <= 0)) {
      showError('Invalid maximum', 'The maximum value must be a number greater than zero.');
      return false;
    }
    if (!value && fieldsRef.current.find(field => field.id === fieldId)?.weight > 0) {
      showError('Maximum needed', 'Remove this question’s contribution before removing its maximum.');
      return false;
    }
    return persist((current) => current.map((field) => {
      if (field.id !== fieldId) return field;
      const next = { ...field };
      if (value) next.maxScore = Number(value);
      else delete next.maxScore;
      return next;
    }));
  };

  const handleCommitWeight = async (fieldId, rawValue) => {
    const value = String(rawValue ?? '').trim();
    if (value && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
      showError('Invalid contribution', 'Use a positive weight, or leave it blank to keep this answer separate.');
      return false;
    }
    return persist(current => current.map(field => {
      if (field.id !== fieldId) return field;
      const next = { ...field };
      if (Number(value) > 0) next.weight = Number(value);
      else delete next.weight;
      return next;
    }));
  };

  const handleRemoveOption = async (fieldId, optionIndex) => {
    const next = fieldsRef.current.map((f) => {
      if (f.id !== fieldId || f.type !== 'multiChoice') return f;
      const opts = (f.options || []).filter((_, i) => i !== optionIndex);
      return { ...f, options: opts };
    });
    await persist(next);
  };

  // ── Conditional reveal ("show this only when…") ────────────────────────
  // Kept deliberately simple: one trigger + one value (equality). A field with
  // a `showWhen` reveals only when an EARLIER yes/no or choice field holds the
  // chosen value. No AND/OR, no compound logic — the renderer reads exactly
  // this single-key map.
  const isTriggerable = (t) => t === 'boolean' || t === 'binary' || t === 'multiChoice';

  const triggerValueOptions = (trigger) => {
    if (!trigger) return [];
    if (trigger.type === 'multiChoice') {
      return (trigger.options || []).map((o) => ({ value: o.value, label: o.label || o.value }));
    }
    return [{ value: true, label: 'Yes' }, { value: false, label: 'No' }];
  };

  const coerceTriggerValue = (trigger, raw) => {
    if (trigger && (trigger.type === 'boolean' || trigger.type === 'binary')) return raw === 'true';
    return raw;
  };

  const handleSetTrigger = async (fieldId, triggerId) => {
    const next = fieldsRef.current.map((f) => {
      if (f.id !== fieldId) return f;
      if (!triggerId) {
        const rest = { ...f };
        delete rest.showWhen;
        return rest;
      }
      const trigger = fields.find((t) => t.id === triggerId);
      const opts = triggerValueOptions(trigger);
      return { ...f, showWhen: { [triggerId]: opts.length ? opts[0].value : null } };
    });
    await persist(next);
  };

  const handleSetTriggerValue = async (fieldId, triggerId, raw) => {
    const trigger = fields.find((t) => t.id === triggerId);
    const next = fieldsRef.current.map((f) =>
      f.id === fieldId ? { ...f, showWhen: { [triggerId]: coerceTriggerValue(trigger, raw) } } : f,
    );
    await persist(next);
  };

  const updateDraftOption = (i, patch) => {
    setDraft((d) => {
      const opts = [...d.optionsDraft];
      opts[i] = { ...opts[i], ...patch };
      return { ...d, optionsDraft: opts };
    });
  };

  const addDraftOption = () => {
    setDraft((d) => ({
      ...d,
      optionsDraft: [...d.optionsDraft, { value: `opt_${Date.now().toString(36)}`, label: '', color: 'neutral' }],
    }));
  };

  const removeDraftOption = (i) => {
    setDraft((d) => ({
      ...d,
      optionsDraft: d.optionsDraft.filter((_, idx) => idx !== i),
    }));
  };

  const addFieldFormId = 'add-domain-event-field';
  const addFieldActions = (
    <>
      <button type="button" className="btn btn-ghost btn-sm" onClick={closeAddForm} disabled={busy}>
        Cancel
      </button>
      <button
        type="submit"
        form={addFieldFormId}
        className="btn btn-primary btn-sm gap-1"
        disabled={busy || !draft.label.trim()}
      >
        <PlusIcon className="h-4 w-4" />
        Add field
      </button>
    </>
  );
  const addFieldForm = (
    <AddFieldDraftForm
      formId={addFieldFormId}
      draft={draft}
      setDraft={setDraft}
      busy={busy}
      updateDraftOption={updateDraftOption}
      removeDraftOption={removeDraftOption}
      addDraftOption={addDraftOption}
      onSubmit={handleAdd}
    />
  );

  if (replaceContentWhenAdding && showAddForm) {
    return (
      <div className="space-y-4" onFocusCapture={handleFocusCapture} onBlurCapture={handleBlurCapture}>
        {addFieldForm}
        <div className="flex justify-end gap-2 border-t border-base-200 pt-4">
          {addFieldActions}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" onFocusCapture={handleFocusCapture} onBlurCapture={handleBlurCapture}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{supplementaryOnly ? 'Additional questions' : 'Event fields'}</h3>
            {saveStatus === 'saving' && <span className="badge badge-info badge-outline badge-sm">Saving…</span>}
            {saveStatus === 'saved' && <span className="badge badge-success badge-outline badge-sm">{onChange ? 'Ready to create' : 'Saved'}</span>}
            {saveStatus === 'error' && <span className={`badge badge-outline badge-sm ${saveConflict ? '' : 'badge-error'}`}>{saveConflict ? 'Review changes' : 'Save failed'}</span>}
          </div>
        <p className="text-xs text-base-content/60">
          {fields.length === 0
            ? supplementaryOnly ? 'Add questions if staff need to record more than the assessment result.' : 'Add the first answer staff will record for each learner.'
            : `${fields.length} input${fields.length === 1 ? '' : 's'} per student.`}
        </p>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm gap-1 self-start"
          onClick={() => setShowAddForm(true)}
          {...(!replaceContentWhenAdding ? { 'aria-haspopup': 'dialog', 'aria-expanded': showAddForm } : {})}
        >
          <PlusIcon className="h-4 w-4" /> Add field
        </button>
      </div>

      {saveStatus === 'error' && !saveConflict && (
        <div className="alert alert-error py-2 text-sm" role="alert">
          <span>{saveError} Your changes are still here.</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => persist((current) => current)}>Retry save</button>
        </div>
      )}
      {saveConflict && (
        <details className="rounded-box border border-base-300 p-3" open>
          <summary className="cursor-pointer font-semibold text-sm">Review conflicting changes</summary>
          <p className="mt-2 text-sm" role="status">This form changed in another session. Your edits are kept below.</p>
          <div className="overflow-x-auto my-3">
            <table className="table table-sm">
              <thead><tr><th>Setting</th><th>Your edit</th><th>Saved</th></tr></thead>
              <tbody>{saveConflict.conflicts.map((conflict, index) => (
                <tr key={index}><td>{conflictLabel(conflict, saveConflict.mergedFields)}</td>
                  <td>{conflictValue(conflict.local, phaseNames, conflict.path.includes('phaseApplicability'))}</td>
                  <td>{conflictValue(conflict.saved, phaseNames, conflict.path.includes('phaseApplicability'))}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <p className="text-xs text-base-content/60 mb-3">Other saved changes are kept when you save your edits.</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => {
              baseFields.current = saveConflict.currentFields;
              acknowledgedDraft.current = null;
              const merged = saveConflict.mergedFields;
              persist([...(supplementaryOnly ? [] : [merged.primary]), ...(merged.followUp || [])]);
            }}>Save my edits</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => {
              baseFields.current = null;
              acknowledgedDraft.current = null;
              fieldsRef.current = remoteFields;
              setPendingFields(null);
              setSaveConflict(null);
              setSaveError(null);
              setSaveStatus('idle');
            }}>Use saved form</button>
          </div>
        </details>
      )}

      {fields.length > 0 && (
        <ul className="space-y-1.5">
          {fields.map((f, idx) => {
            const triggerId = f.showWhen ? Object.keys(f.showWhen)[0] : '';
            const trigger = triggerId ? fields.find((t) => t.id === triggerId) : null;
            // Triggers must be EARLIER fields (a field can't depend on one asked
            // after it) and must be yes/no or choice (string/number aren't offered).
            const eligibleTriggers = fields.slice(0, idx).filter((t) => isTriggerable(t.type));
            const showNesting = idx > 0 && eligibleTriggers.length > 0;
            return (
            <li
              key={f.id}
              className={`rounded-lg bg-base-100 border border-base-300 overflow-hidden${
                f.showWhen ? ' border-l-4 border-l-primary/40' : ''
              }`}
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2 px-3 py-2">
                <FieldValueInput
                  type="text"
                  className="input input-bordered input-sm min-w-36 flex-1"
                  aria-label={`Field name for ${f.id}`}
                  value={f.label}
                  onCommit={(value) => handleCommitFieldLabel(f.id, value)}
                />
                <span className="badge badge-ghost badge-sm">
                  {FIELD_TYPE_LABELS[f.type] || f.type}
                  {f.type === 'number' && f.maxScore ? ` /${f.maxScore}` : ''}
                  {f.type === 'multiChoice' ? ` · ${onChange ? (f.options || []).length : phaseView.filter(f.options).length}` : ''}
                </span>
                {fields.length > 1 && (
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-square"
                      onClick={() => handleMove(f.id, -1)}
                      disabled={busy || idx === 0}
                      aria-label={`Move ${f.label} up`}
                    >
                      <ArrowUpIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-square"
                      onClick={() => handleMove(f.id, 1)}
                      disabled={busy || idx === fields.length - 1}
                      aria-label={`Move ${f.label} down`}
                    >
                      <ArrowDownIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {(fields.length > 1 || supplementaryOnly) ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square"
                    onClick={() => handleRemove(f.id)}
                    disabled={busy}
                    aria-label={`Remove ${f.label}`}
                  >
                    <TrashIcon className="w-4 h-4 text-error/70" />
                  </button>
                ) : (
                  <span className="badge badge-ghost badge-sm" title="A Domain needs at least one Event field">Required</span>
                )}
              </div>
              {(supplementaryOnly || idx > 0 || f.type === 'number') && (
                <div className="flex flex-col gap-3 border-t border-base-200 bg-base-200/20 px-3 py-2.5 sm:flex-row sm:items-end">
                  {(supplementaryOnly || idx > 0) && (
                    <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-xs"
                        checked={f.optional === true}
                        disabled={busy}
                        onChange={(event) => handleToggleOptional(f.id, event.target.checked)}
                      />
                      Optional question
                    </label>
                  )}
                  {f.type === 'number' && (
                    <label className="flex min-w-0 flex-col gap-1 text-xs font-medium sm:ml-auto sm:w-32">
                      Maximum value
                      <FieldValueInput
                        type="number"
                        min="0.000001"
                        step="any"
                        className="input input-bordered input-xs w-full"
                        aria-label={`Maximum value for ${f.label}`}
                        value={f.maxScore || ''}
                        placeholder="No maximum"
                        onCommit={(value) => handleCommitMaxScore(f.id, value)}
                      />
                    </label>
                  )}
                </div>
              )}
              {f.type === 'multiChoice' && (
                <div className="px-3 pb-3 border-t border-base-200 bg-base-200/40">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-base-content/50 pt-2 pb-1.5">
                    Choices
                  </p>
                  <ul className="space-y-1.5">
                    {(f.options || []).map((opt, i) => (
                      <OptionRow
                        key={`${f.id}-opt-${opt.value}`}
                        fieldId={f.id}
                        optionIndex={i}
                        option={opt}
                        hidden={!onChange && !phaseView.matches(opt)}
                        busy={busy}
                        canRemove={(f.options?.length || 0) > 2}
                        onCommit={handleCommitOptionLabel}
                        onColor={handleCommitOptionColor}
                        onRemove={handleRemoveOption}
                        onPhase={onChange || canConfigureDomain(activeDomain, 'event', 'edit', 'field.options.phaseApplicability')
                          ? openPhaseEditor : undefined}
                      />
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1 mt-2"
                    onClick={() => handleAddOption(f.id)}
                    disabled={busy}
                  >
                    <PlusIcon className="w-3 h-3" />
                    Add choice
                  </button>
                </div>
              )}
              {showNesting && (
                <div className="px-3 pb-3 pt-2 border-t border-base-200 bg-base-200/30 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-base-content/60">Only show when</span>
                  <select
                    className="select select-bordered select-xs"
                    value={triggerId}
                    onChange={(e) => handleSetTrigger(f.id, e.target.value)}
                    disabled={busy}
                  >
                    <option value="">always</option>
                    {eligibleTriggers.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                  {trigger && (
                    <>
                      <span className="text-[11px] text-base-content/60">is</span>
                      <select
                        className="select select-bordered select-xs"
                        value={String(f.showWhen[triggerId])}
                        onChange={(e) => handleSetTriggerValue(f.id, triggerId, e.target.value)}
                        disabled={busy}
                      >
                        {triggerValueOptions(trigger).map((o) => (
                          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}

      {!supplementaryOnly && !isAssessmentDomain(activeDomain) && activeDomain.runtime?.protected !== true
        && fields.filter(field => field.type === 'number').length >= 2
        && (onChange || canConfigureDomain(activeDomain, 'event', 'edit', 'field.weight')) && (
        <details className="rounded-xl border border-base-300 bg-base-100 p-4">
          <summary className="cursor-pointer text-sm font-semibold">Combined total</summary>
          <p className="mt-2 text-xs text-base-content/60">
            Give at least two scores a contribution to combine their percentages. A weight of 2 counts twice as much as 1. Leave other answers blank to keep them separate.
          </p>
          <div className="mt-3 flex flex-col gap-3">
            {fields.filter(field => field.type === 'number').map(field => (
              <label key={field.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0">{field.label}
                  {!field.maxScore && <span className="block text-xs text-base-content/60">Set a maximum value first.</span>}
                </span>
                <FieldValueInput type="number" min="0" step="any"
                  className="input input-bordered input-sm w-28 shrink-0"
                  aria-label={`Contribution for ${field.label}`} value={field.weight ?? ''}
                  placeholder="Separate" disabled={busy || !field.maxScore}
                  onCommit={value => handleCommitWeight(field.id, value)} />
              </label>
            ))}
          </div>
        </details>
      )}

      {fields.length > 0 && (
        <section className="min-w-0 overflow-hidden rounded-xl border border-base-300 bg-base-100 p-4" aria-labelledby="staff-event-preview">
          <h4 id="staff-event-preview" className="text-sm font-semibold">Staff preview</h4>
          <p className="mt-0.5 text-xs text-base-content/55">A compact preview of what staff answer for one learner. {!onChange && phaseView.filtered ? 'Choices follow the selected phase view.' : ''}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {fields.map((field) => (
              <div key={`preview-${field.id}`} className="flex min-w-0 flex-col gap-1.5">
                <label className="text-xs font-medium">{field.label}</label>
                {field.type === 'multiChoice' ? (
                  <select className="select select-bordered select-sm min-w-0 w-full" defaultValue="" disabled>
                    <option value="">Choose…</option>
                    {(onChange ? (field.options || []) : phaseView.filter(field.options)).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : field.type === 'boolean' || field.type === 'binary' ? (
                  <div className="join w-full" aria-disabled="true">
                    <button type="button" className="btn btn-sm join-item flex-1" disabled>Yes</button>
                    <button type="button" className="btn btn-sm join-item flex-1" disabled>No</button>
                  </div>
                ) : field.type === 'storageRef' ? (
                  <input className="file-input file-input-bordered file-input-sm min-w-0 w-full" type="file" disabled />
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    className="input input-bordered input-sm min-w-0 w-full"
                    placeholder={field.type === 'number' && field.maxScore ? `Up to ${field.maxScore}` : 'Staff answer'}
                    disabled
                  />
                )}
                {field.showWhen && (
                  <span className="text-[11px] text-base-content/50">Shown only after the matching earlier answer.</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <Modal
        isOpen={showAddForm}
        nativeDialog
        size="md"
        title="Add an event field"
        onClose={closeAddForm}
        closeDisabled={busy}
        actions={addFieldActions}
      >
        {addFieldForm}
      </Modal>
      <Modal isOpen={Boolean(phaseEditor)} nativeDialog size="md"
        title={`Phase availability: ${phaseEditor?.label || 'Choice'}`}
        onClose={closePhaseEditor} closeDisabled={busy}
        actions={<>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={closePhaseEditor}>Cancel</button>
          <button type="submit" form={phaseFormId} className="btn btn-primary btn-sm" disabled={busy}>Save choice phases</button>
        </>}>
        {phaseEditor && <form id={phaseFormId} onSubmit={saveChoicePhases} className="space-y-3">
          <PhaseApplicabilityField schoolId={schoolId} value={phaseEditor.value}
            domainApplicability={activeDomain.phaseApplicability} disabled={busy}
            onChange={value => { setPhaseEditor(current => ({ ...current, value })); setPhaseError(''); }} />
          <p className="text-xs text-base-content/65">This stays one shared choice. Phase settings take effect after phase processes are reviewed and activated.</p>
          {phaseError && <p role="alert" className="text-sm text-error">{phaseError}</p>}
        </form>}
      </Modal>
    </div>
  );
}
